import { A } from "@solidjs/router";
import { For, Show } from "solid-js";
import { BRIEF_SOURCE_TYPE } from "../../enums/brief";
import { GOVERNANCE_STATE } from "../../enums/govern";
import type { DeliveryBrief } from "../brief/briefTypes";
import {
  governanceNextAction,
  verificationSummaryMessage,
} from "./governGuidance";
import {
  governanceStateLabel,
  isAssigned,
  verificationSummary,
} from "./governStatus";
import type { GovernWorkItem } from "./governTypes";

export function GovernRail(props: {
  work: GovernWorkItem;
  currentBrief: DeliveryBrief | null;
}) {
  const summary = () => verificationSummary(props.work);
  const nextAction = () => governanceNextAction(props.work, props.currentBrief);
  const assignedRoles = () =>
    Object.entries(props.work.roles).filter(([, value]) => isAssigned(value));
  return (
    <aside class="govern-status-rail" aria-label="Governance status and source">
      <section class="govern-status-card">
        <span class="signal-mini-kicker">Current state</span>
        <h2>{governanceStateLabel(props.work.state)}</h2>
        <p aria-live="polite">{nextAction().message}</p>
      </section>

      <section class="govern-rail-section">
        <h3>Source Brief</h3>
        <strong>{props.currentBrief?.status ?? "Unavailable"}</strong>
        <p>{props.work.sourceSnapshot.source.label}</p>
        <A href={`/w/${props.work.websiteId}/brief`}>Open Brief</A>
        <A href={props.work.sourceSnapshot.source.route}>
          Open{" "}
          {props.work.sourceType === BRIEF_SOURCE_TYPE.ISSUE
            ? "Scan issue"
            : "Map change"}
        </A>
      </section>

      <section class="govern-rail-section">
        <h3>Assigned roles</h3>
        <Show
          when={assignedRoles().length > 0}
          fallback={<p>No governance roles are assigned.</p>}
        >
          <dl>
            <For each={assignedRoles()}>
              {([role, value]) => (
                <div>
                  <dt>{roleLabel(role)}</dt>
                  <dd>{value}</dd>
                </div>
              )}
            </For>
          </dl>
        </Show>
      </section>

      <section class="govern-rail-section">
        <h3>Verification</h3>
        <ul class="govern-rail-counts">
          <li>
            <strong>{summary().passed}</strong> passed
          </li>
          <li>
            <strong>{summary().failed}</strong> failed
          </li>
          <li>
            <strong>{summary().unchecked}</strong> not checked
          </li>
          <Show when={summary().notApplicable > 0}>
            <li>
              <strong>{summary().notApplicable}</strong> not applicable
            </li>
          </Show>
        </ul>
        <p>{verificationSummaryMessage(props.work)}</p>
      </section>

      <section class="govern-rail-section">
        <h3>Next review</h3>
        <Show
          when={props.work.reviewSchedule}
          fallback={<p>Next review not scheduled.</p>}
        >
          {(schedule) => (
            <Show
              when={props.work.state === GOVERNANCE_STATE.REVIEW_SCHEDULED}
              fallback={
                <p>
                  Previous schedule retained. Reverify the work before it
                  becomes active.
                </p>
              }
            >
              <strong>{schedule().nextReviewDate}</strong>
              <p>{schedule().reviewOwner}</p>
            </Show>
          )}
        </Show>
      </section>

      <details class="govern-rail-technical">
        <summary>Technical detail</summary>
        <dl>
          <div>
            <dt>Work ID</dt>
            <dd>{props.work.id}</dd>
          </div>
          <div>
            <dt>Source Brief ID</dt>
            <dd>{props.work.sourceBriefId}</dd>
          </div>
          <div>
            <dt>Opened</dt>
            <dd>{new Date(props.work.openedAt).toLocaleString()}</dd>
          </div>
        </dl>
      </details>
    </aside>
  );
}

function roleLabel(role: string): string {
  const labels: Record<string, string> = {
    deliveryOwner: "Delivery owner",
    reviewer: "Reviewer",
    approver: "Approver",
    verifier: "Verifier",
    reviewOwner: "Review owner",
  };
  return labels[role] ?? role;
}
